// ProgressiveSectionReveal.jsx
import React, { useState, useEffect } from 'react';
import { ChevronRight, Check, Grid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
const ProgressiveSectionReveal = ({
  sections,
  currentSection,
  onSectionComplete,
  isMobile,
  onOpenSectionsMenu // Callback to expose sidebar toggle to parent
}) => {

  const { t } = useTranslation(); // <-- ADD THIS
  const [completedSections, setCompletedSections] = useState(new Set());
  const [expandedSection, setExpandedSection] = useState(currentSection);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Expose sidebar toggle function to parent via callback
  useEffect(() => {
    if (onOpenSectionsMenu) {
      onOpenSectionsMenu.current = () => setIsSidebarOpen(true);
    }
  }, [onOpenSectionsMenu]);

  useEffect(() => {
    setExpandedSection(currentSection);
  }, [currentSection]);

  useEffect(() => {
    const newCompleted = new Set();
    // All sections *before* the current one are considered complete.
    for (let i = 0; i < currentSection; i++) {
      newCompleted.add(i);
    }
    setCompletedSections(newCompleted);
  }, [currentSection]);


  const handleSectionComplete = (sectionIndex) => {
    // First, mark the *current* section as complete.
    const newCompleted = new Set(completedSections);
    newCompleted.add(sectionIndex);
    setCompletedSections(newCompleted);

    // Then, auto-advance to the next section via the parent callback.
    if (sectionIndex < sections.length - 1) {
      setTimeout(() => {
        onSectionComplete(sectionIndex + 1);
      }, 500); // A small delay for a smoother feel
    }
  };

  const styles = {
    // ... all your styles remain exactly the same
    container: {
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 0,
  marginTop: 0,
  padding: 0,
},
    sidebar: {
      width: isMobile ? '100%' : '280px',
      flexShrink: 0
    },
   progressBar: {
      background: '#FFFFFF',
      backgroundImage: `
        linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent),
        linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent)
      `,
      backgroundSize: '80px 80px',
      borderRadius: '0.5rem',
      padding: '1.5rem',
      marginBottom: '1rem',
      border: '2px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    progressTitle: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#6b7280',
      marginBottom: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    progressTrack: {
      height: '8px',
      background: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '0.5rem'
    },
    progressFill: {
  height: '100%',
  background: 'linear-gradient(90deg, #000000 0%, #4a4a4a 100%)',
  borderRadius: '4px',
  transition: 'width 0.5s ease',
  position: 'relative',
  overflow: 'hidden',
},
    progressText: {
      fontSize: '0.875rem',
      color: '#374151'
    },
    sectionNav: {
      background: '#FFFFFF',
      backgroundImage: `
        linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent),
        linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent)
      `,
      backgroundSize: '80px 80px',
      borderRadius: '0.75rem',
      padding: '0.5rem',
      border: '2px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    sectionNavItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '0.75rem 1rem',
      marginBottom: '0.25rem',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative'
    },
    sectionNumber: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '0.75rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      transition: 'all 0.3s ease'
    },
    sectionTitle: {
      flex: 1,
      fontSize: '0.875rem',
      fontWeight: '500',
      transition: 'color 0.2s ease'
    },
  content: {
  flex: 1,
  minWidth: 0,
  padding: 0,
},
   sectionContent: {
  background: '#FFFFFF',
  backgroundImage: `
    linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .02) 25%, rgba(0, 0, 0, .02) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .02) 75%, rgba(0, 0, 0, .02) 76%, transparent 77%, transparent),
    linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .02) 25%, rgba(0, 0, 0, .02) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .02) 75%, rgba(0, 0, 0, .02) 76%, transparent 77%, transparent)
  `,
  backgroundSize: '80px 80px',
  borderRadius: isMobile ? '0.5rem' : '1rem',
  padding: isMobile ? '0.75rem' : '2rem',
  display: 'flex',
  flexDirection: 'column',
  margin: 0,
  width: '100%',
  boxSizing: 'border-box',
  boxShadow: isMobile ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
  marginBottom: isMobile ? '0.5rem' : '1rem',
  opacity: 0,
  animation: 'fadeInUp 0.6s ease-out forwards'
},
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      // ✅ MODIFIED: Reduced spacing for mobile
      marginBottom: isMobile ? '1rem' : '1.5rem',
      paddingBottom: isMobile ? '0.75rem' : '1rem',
      borderBottom: '2px solid #f3f4f6'
    },
   continueButton: {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.75rem',
  background: '#000000',
  color: 'white',
  border: '3px solid #000000',
  padding: isMobile ? '0.75rem 1.5rem' : '1rem 2rem',
  borderRadius: '1rem',
  fontSize: isMobile ? '1rem' : '1.125rem',
  fontWeight: '700',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  marginTop: isMobile ? '1rem' : '1.5rem',
  alignSelf: isMobile ? 'center' : 'flex-start',
  width: 'auto',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
},
    mobileProgress: {
      position: isMobile ? 'sticky' : 'relative',
      top: isMobile ? '0' : 'auto',
      background: 'white',
      zIndex: 10,
      padding: isMobile ? '1rem' : '0',
      marginBottom: '1rem',
      boxShadow: isMobile ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
    }
  };

  // --- ✅ progressPercentage now works correctly ---
  // The story is section 0. Once currentSection is 1, this will calculate progress.
  const progressPercentage = (completedSections.size / sections.length) * 100;

  const getNavItemStyle = (index) => {
    // ... no changes here
    const isActive = index === expandedSection;
    const isCompleted = completedSections.has(index);
    const isAccessible = index <= currentSection;

    return {
  ...styles.sectionNavItem,
  background: isActive ? '#000000' :
              isCompleted ? '#FFFFFF' :
              isAccessible ? '#F9FAFB' : '#F9FAFB',
  border: isActive ? '2px solid #000000' :
          isCompleted ? '2px solid #000000' :
          '2px solid #E5E7EB',
  cursor: isAccessible ? 'pointer' : 'not-allowed',
  opacity: isAccessible ? 1 : 0.5
};
  };

  const getNumberStyle = (index) => {
    // ... no changes here
    const isActive = index === expandedSection;
    const isCompleted = completedSections.has(index);
    const isAccessible = index <= currentSection;

    return {
  ...styles.sectionNumber,
  background: isActive ? 'rgba(255,255,255,0.2)' :
              isCompleted ? '#000000' :
              '#E5E7EB',
  color: isActive ? 'white' :
         isCompleted ? 'white' :
         isAccessible ? '#000000' : '#9CA3AF'
};
  };

  return (
    <>
      {/* ... The entire JSX return remains exactly the same ... */}
     {isMobile && (
  <div style={styles.mobileProgress}>
   {/* Grid button removed from here - now in bottom nav */}
    <div style={styles.progressTrack}>
            <div style={{
              ...styles.progressFill,
              width: `${progressPercentage}%`
            }} />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '0.5rem'
          }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
  {t('grammar.reveal.sectionOf', { current: expandedSection + 1, total: sections.length })}
</span>
           <span style={{ fontSize: '0.875rem', color: '#000000', fontWeight: '700' }}>
  {t('grammar.reveal.percentComplete', { percent: Math.round(progressPercentage) })}
</span>
          </div>
        </div>
      )}

     <div style={styles.container}>
  {isMobile && isSidebarOpen && (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setIsSidebarOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999,
          animation: 'fadeIn 0.3s ease-out'
        }}
      />

      {/* Slide-out Sidebar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: '280px',
        maxWidth: '80vw',
        background: 'white',
        zIndex: 1000,
        overflowY: 'auto',
        boxShadow: '4px 0 12px rgba(0,0,0,0.15)',
        animation: 'slideInFromLeft 0.3s ease-out',
        padding: '1rem'
      }}>
        {/* Close button */}
        <button
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'transparent',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '0.5rem'
          }}
        >
          ×
        </button>

        {/* Progress Bar */}
        <div style={{...styles.progressBar, marginTop: '2rem'}}>
          <div style={styles.progressTitle}>{t('grammar.reveal.yourProgress')}</div>
          <div style={styles.progressTrack}>
  <div style={{
    ...styles.progressFill,
    width: `${progressPercentage}%`
  }}>
    {/* Shimmer effect */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
      animation: 'shimmer 2s infinite',
    }} />
  </div>
</div>
          <div style={styles.progressText}>
            {t('grammar.reveal.sectionsComplete', { completed: completedSections.size, total: sections.length })}
          </div>
        </div>

        {/* Section Navigation */}
        <div style={styles.sectionNav}>
          {sections.map((section, index) => (
            <div
              key={index}
              style={getNavItemStyle(index)}
              onClick={() => {
                if (index <= currentSection) {
                  setExpandedSection(index);
                  setIsSidebarOpen(false);
                }
              }}
            >
              <div style={getNumberStyle(index)}>
                {completedSections.has(index) ? <Check size={16} /> : index + 1}
              </div>
              <div style={{
  ...styles.sectionTitle,
  color: index === expandedSection ? 'white' :
         completedSections.has(index) ? '#000000' :
         '#374151'
}}>
  {section.title}
</div>
              {index === expandedSection && <ChevronRight size={18} color="white" />}
            </div>
          ))}
        </div>
      </div>
    </>
  )}

  {!isMobile && (
          <div style={styles.sidebar}>
            <div style={styles.progressBar}>
              <div style={styles.progressTitle}>{t('grammar.reveal.yourProgress')}</div>
              <div style={styles.progressTrack}>
                <div style={{
                  ...styles.progressFill,
                  width: `${progressPercentage}%`
                }} />
              </div>
              <div style={styles.progressText}>
  {t('grammar.reveal.sectionsComplete', { completed: completedSections.size, total: sections.length })}
</div>
            </div>

            <div style={styles.sectionNav}>
              {sections.map((section, index) => (
                <div
  key={index}
  style={{
    ...getNavItemStyle(index),
    position: 'relative',
    overflow: 'hidden'
  }}
  onClick={() => index <= currentSection && setExpandedSection(index)}
  className="section-nav-item"
>
  {/* ✨ Decorative dots (appear on hover) */}
  <div style={{
    position: 'absolute',
    top: '8px',
    left: '8px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#3A86FF',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    pointerEvents: 'none'
  }} className="nav-dot-1" />

  <div style={{
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#3A86FF',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    transitionDelay: '0.1s',
    pointerEvents: 'none'
  }} className="nav-dot-2" />

  <div style={{
    position: 'absolute',
    bottom: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#3A86FF',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    transitionDelay: '0.2s',
    pointerEvents: 'none'
  }} className="nav-dot-3" />

  {/* ✨ Shine effect on hover */}
  <div style={{
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(45deg, transparent, rgba(58, 134, 255, 0.1), transparent)',
    transform: 'translateX(-100%)',
    transition: 'transform 0.6s ease',
    pointerEvents: 'none'
  }} className="nav-shine" />

  {/* Original content */}
  <div style={getNumberStyle(index)}>
    {completedSections.has(index) ?
      <Check size={16} /> :
      index + 1
    }
  </div>
  <div style={{
    ...styles.sectionTitle,
    color: index === expandedSection ? 'white' :
           completedSections.has(index) ? '#000000' :
           '#374151'
  }}>
    {section.title}
  </div>
  {index === expandedSection && (
    <ChevronRight size={18} color="white" />
  )}
</div>
              ))}
            </div>
          </div>
        )}

        <div style={styles.content}>
          {sections.map((section, index) => (
            index === expandedSection && index <= currentSection && (
              <div key={index} style={styles.sectionContent}>
                <div style={styles.sectionHeader}>
                  <div style={{
  ...styles.sectionNumber,
  background: '#000000',
  color: 'white',
  marginRight: '1rem'
}}>
                    {index + 1}
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {section.title}
                  </h3>
                </div>

                {section.content}

                {/* --- ✅ The "Continue" button is now hidden for the story --- */}
                {/* It is only shown if a section is not yet complete. The story (section 0)
                    will auto-complete, so it never needs this button. */}
              {index <= currentSection && (
  <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
    <button
      style={{
        ...styles.continueButton,
        cursor: 'pointer',
      }}
      onClick={() => handleSectionComplete(index)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = 'scale(0.98)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
      }}
      className="continue-button-animated"
    >
      {/* Shimmer effect on hover */}
      <div
        className="shimmer-effect"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
          backgroundSize: '200% 100%',
          opacity: 0,
          transition: 'opacity 0.3s',
          pointerEvents: 'none',
        }}
      />

      {/* Particle burst on hover */}
      <div className="particle-container" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'white',
              borderRadius: '50%',
              left: '50%',
              top: '50%',
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* Corner indicators */}
      <div className="corner-top-left" style={{
        position: 'absolute',
        top: '4px',
        left: '4px',
        width: '12px',
        height: '12px',
        borderTop: '2px solid white',
        borderLeft: '2px solid white',
        opacity: 0,
        transition: 'opacity 0.3s',
      }} />
      <div className="corner-bottom-right" style={{
        position: 'absolute',
        bottom: '4px',
        right: '4px',
        width: '12px',
        height: '12px',
        borderBottom: '2px solid white',
        borderRight: '2px solid white',
        opacity: 0,
        transition: 'opacity 0.3s',
      }} />

      <span style={{ position: 'relative', zIndex: 1, fontWeight: '700' }}>
        {t('button.continue')}
      </span>
      <ChevronRight size={18} style={{ position: 'relative', zIndex: 1 }} />
    </button>
  </div>
)}
              </div>
            )
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* Continue Button Animations */
.continue-button-animated:hover .shimmer-effect {
  opacity: 1;
  animation: shimmer 1.5s infinite;
}

.continue-button-animated:hover .corner-top-left,
.continue-button-animated:hover .corner-bottom-right {
  opacity: 1;
}

.continue-button-animated:hover .particle {
  animation: particleFloat 0.8s ease-out forwards;
}

.continue-button-animated .particle:nth-child(1) { animation-delay: 0s; }
.continue-button-animated .particle:nth-child(2) { animation-delay: 0.05s; }
.continue-button-animated .particle:nth-child(3) { animation-delay: 0.1s; }
.continue-button-animated .particle:nth-child(4) { animation-delay: 0.15s; }
.continue-button-animated .particle:nth-child(5) { animation-delay: 0.2s; }
.continue-button-animated .particle:nth-child(6) { animation-delay: 0.25s; }
.continue-button-animated .particle:nth-child(7) { animation-delay: 0.3s; }
.continue-button-animated .particle:nth-child(8) { animation-delay: 0.35s; }
.continue-button-animated .particle:nth-child(9) { animation-delay: 0.4s; }
.continue-button-animated .particle:nth-child(10) { animation-delay: 0.45s; }
.continue-button-animated .particle:nth-child(11) { animation-delay: 0.5s; }
.continue-button-animated .particle:nth-child(12) { animation-delay: 0.55s; }

@keyframes particleFloat {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
  }
  100% {
    transform: translate(
      calc(-50% + var(--tx, 0)),
      calc(-50% + var(--ty, 0))
    ) scale(1);
    opacity: 0;
  }
}
/* Section Navigation Hover Effects */
.section-nav-item:hover .nav-dot-1,
.section-nav-item:hover .nav-dot-2,
.section-nav-item:hover .nav-dot-3 {
  opacity: 1 !important;
}

.section-nav-item:hover .nav-shine {
  transform: translateX(100%) !important;
}

.section-nav-item {
  transition: all 0.3s ease;
}

.section-nav-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(58, 134, 255, 0.2) !important;
}

/* Particle positions */
.continue-button-animated .particle:nth-child(1) { --tx: 60px; --ty: 0px; }
.continue-button-animated .particle:nth-child(2) { --tx: 52px; --ty: 30px; }
.continue-button-animated .particle:nth-child(3) { --tx: 30px; --ty: 52px; }
.continue-button-animated .particle:nth-child(4) { --tx: 0px; --ty: 60px; }
.continue-button-animated .particle:nth-child(5) { --tx: -30px; --ty: 52px; }
.continue-button-animated .particle:nth-child(6) { --tx: -52px; --ty: 30px; }
.continue-button-animated .particle:nth-child(7) { --tx: -60px; --ty: 0px; }
.continue-button-animated .particle:nth-child(8) { --tx: -52px; --ty: -30px; }
.continue-button-animated .particle:nth-child(9) { --tx: -30px; --ty: -52px; }
.continue-button-animated .particle:nth-child(10) { --tx: 0px; --ty: -60px; }
.continue-button-animated .particle:nth-child(11) { --tx: 30px; --ty: -52px; }
.continue-button-animated .particle:nth-child(12) { --tx: 52px; --ty: -30px; }
      `}</style>
    </>
  );
};

export default ProgressiveSectionReveal;